import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { useCompanySettings } from './hooks/useCompanySettings';
import { canSeeDispatchMoney } from './lib/supabase';
import { LoginPage } from './components/auth/LoginPage';
import { PasswordRecoveryPage } from './components/auth/PasswordRecoveryPage';
import { ProfileLoadError } from './components/auth/ProfileLoadError';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { CompactLayout } from './components/layout/CompactLayout';
import { AdvancedDashboard } from './components/dashboard/AdvancedDashboard';
import { AdvancedAnalyticsView } from './components/analytics/AdvancedAnalyticsView';
import { LoadsView } from './components/loads/LoadsView';
import { DispatchesView } from './components/loads/DispatchesView';
import { DispatchDetailView } from './components/loads/DispatchDetailView';
import { EditDispatchPage } from './components/loads/EditDispatchPage';
import { LoadDetailView } from './components/loads/LoadDetailView';
import { TrackingView } from './components/loads/TrackingView';
import { TruckPhotosView as LoadsTruckPhotosView } from './components/loads/TruckPhotosView';
import { TrailerPhotosView as LoadsTrailerPhotosView } from './components/loads/TrailerPhotosView';
import { DriverCdlTwicView } from './components/loads/DriverCdlTwicView';
import { AIAnalyticsView } from './components/loads/AIAnalyticsView';
import { HistoryView } from './components/fleet/HistoryView';
import { CreateLoadPageComprehensive } from './components/loads/CreateLoadPageComprehensive';
import { EditLoadPage } from './components/loads/EditLoadPage';
import { SamsaraLocationView } from './components/tracking/SamsaraLocationView';
import { ELDLocationView } from './components/tracking/ELDLocationView';
import ELDPage from './pages/ELDPage';
import { TruckTrackingPage } from './components/tracking/TruckTrackingPage';
import { DriversView } from './components/drivers/DriversView';
import { CreateDriverPage } from './components/drivers/CreateDriverPage';
import { EditDriverPage } from './components/drivers/EditDriverPage';
import { DriverDetailPage } from './components/drivers/DriverDetailPage';
import { CreateTruckPage } from './components/fleet/CreateTruckPage';
import { EditTruckPage } from './components/fleet/EditTruckPage';
import { CreateTrailerPage } from './components/fleet/CreateTrailerPage';
import { EditTrailerPage } from './components/fleet/EditTrailerPage';
import { TruckDetailPage } from './components/fleet/TruckDetailPage';
import { TrailerDetailPage } from './components/fleet/TrailerDetailPage';
import { FleetView } from './components/fleet/FleetView';
import { TrailersView } from './components/fleet/TrailersView';
import { MaintenanceView } from './components/maintenance/MaintenanceView';
import { MaintenanceStatistics } from './components/maintenance/MaintenanceStatistics';
import { SamsaraDiagnosticsView } from './components/maintenance/SamsaraDiagnosticsView';
import { KeyHiderView } from './components/maintenance/KeyHiderView';
import { TruckPhotosView } from './components/maintenance/TruckPhotosView';
import { TrailerPhotosView } from './components/maintenance/TrailerPhotosView';
import { TruckCleaningView } from './components/maintenance/TruckCleaningView';
import { DocumentsView } from './components/documents/DocumentsView';
import { ActivityLogView } from './components/activity/ActivityLogView';
import { SettingsView } from './components/settings/SettingsView';
import { UserManagementView } from './components/settings/UserManagementView';
// import { DispatchBoard } from './components/dispatch/DispatchBoard'; // Removed - using Excel page as dispatch board
import { InspectionsView } from './components/safety/InspectionsView';
import { DayOffView } from './components/safety/DayOffView';
import { ELDView } from './components/safety/ELDView';
import { CompaniesView } from './components/safety/CompaniesView';
import { CustomersView } from './components/address-book/CustomersView';
import { EditCustomerPage } from './components/address-book/EditCustomerPage';
import { FacilityLocationsView } from './components/address-book/FacilityLocationsView';
import { EditFacilityLocationPage } from './components/address-book/EditFacilityLocationPage';
import { FactoringCompaniesView } from './components/address-book/FactoringCompaniesView';
import { EditFactoringCompanyPage } from './components/address-book/EditFactoringCompanyPage';
import { ServiceProvidersView } from './components/address-book/ServiceProvidersView';
import { EditServiceProviderPage } from './components/address-book/EditServiceProviderPage';
import { HotelReimbursementsView } from './components/accounting/HotelReimbursementsView';
import { LyftReimbursementsView } from './components/accounting/LyftReimbursementsView';
import { AirplaneReimbursementsView } from './components/accounting/AirplaneReimbursementsView';
import { AirplaneTicketsRecView } from './components/safety/AirplaneTicketsRecView';
import { LiftRecView } from './components/safety/LiftRecView';
import { DeductionsView } from './components/accounting/DeductionsView';
import { DeductionsMaintenanceView } from './components/accounting/DeductionsMaintenanceView';
import { CreditsView } from './components/accounting/CreditsView';
import { ScheduledDeductionsView } from './components/accounting/ScheduledDeductionsView';
import { FuelView } from './components/accounting/FuelView';
import { StatementsView } from './components/accounting/StatementsView';
import { ReadyForBillingView } from './components/accounting/ReadyForBillingView';
import { TabelaView } from './components/accounting/TabelaView';
import { IFTAView } from './components/accounting/IFTAView';
import { AccountingView } from './components/accounting/AccountingView';
import { AIAssistantChat } from './components/AIAssistantChat';
import { HelpCenterView } from './components/help/HelpCenterView';
import { SupportTicketsView } from './components/support/SupportTicketsView';
import { MyTicketsView } from './components/support/MyTicketsView';
import { NotificationsPage } from './components/notifications/NotificationsPage';
import { RecruiterAdminDashboard } from './components/recruiting/RecruiterAdminDashboard';
import { RecruiterDailyCRM } from './components/recruiting/RecruiterDailyCRM';
import { RecruiterPerformanceView } from './components/recruiting/RecruiterPerformanceView';
import { NewTrucksView } from './components/company-accounting/NewTrucksView';
import { CompanyExpensesView } from './components/company-accounting/CompanyExpensesView';
import { Company1099View } from './components/company-accounting/Company1099View';
import { Company1099DriverDetailView } from './components/company-accounting/Company1099DriverDetailView';
import { RecruitingAllDriversView } from './components/recruiting/RecruitingAllDriversView';
import { LeadsView } from './components/recruiting/LeadsView';
import { DolasciOdlasciView } from './components/recruiting/DolasciOdlasciView';
import { UnlockAccountView } from './components/admin/UnlockAccountView';
import { DispatchBoardView } from './components/dispatch/DispatchBoardView';
import { DispatchersGrossView } from './components/dispatch/DispatchersGrossView';
import { EmployeeStatementsView } from './components/accounting/EmployeeStatementsView';
import { EmployeeBonusesView } from './components/accounting/EmployeeBonusesView';
import { EmployeeExpensesView } from './components/accounting/EmployeeExpensesView';

function AppContent() {
  const { user, profile, loading, profileError, retryProfile, passwordRecovery, signOut } = useAuth();
  const { companySettings } = useCompanySettings();
  const [currentView, setCurrentView] = useState(() => {
    // Get initial view from URL path
    const path = window.location.pathname.slice(1); // Remove leading slash
    return path || 'dashboard';
  });

  // Update URL when view changes
  useEffect(() => {
    const newPath = currentView === 'dashboard' ? '/' : `/${currentView}`;
    if (window.location.pathname !== newPath) {
      window.history.pushState({}, '', newPath);
    }
  }, [currentView]);

  // SMS polling is now handled per-page (only on dispatch page)
  // Removed global polling to reduce unnecessary API calls

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.slice(1);
      setCurrentView(path || 'dashboard');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (passwordRecovery) {
    return <PasswordRecoveryPage />;
  }

  if (!user) {
    return <LoginPage />;
  }

  // Authenticated but the profile failed to load — show a retry screen rather
  // than rendering the app in a degraded "no role" state.
  if (profileError && !profile) {
    return <ProfileLoadError onRetry={retryProfile} onSignOut={signOut} />;
  }

  const handleMaintenanceLogView = (logId: string) => {
    setCurrentView(`maintenance/${logId}`);
  };

  const hasDispatchersGrossAccess = canSeeDispatchMoney(profile?.role);

  const renderView = () => {
    // Handle dynamic company fleet views (e.g., fleet-cs national, fleet-apollo)
    if (currentView.startsWith('fleet-') && currentView !== 'fleet') {
      const companyCode = currentView.substring(6).toUpperCase().replace(/\s+/g, ''); // Remove 'fleet-' prefix and normalize
      return <FleetView companyFilter={companyCode} />;
    }
    
    // Handle dynamic company driver views (e.g., drivers-cs national, drivers-apollo)
    if (currentView.startsWith('drivers-') && currentView !== 'drivers') {
      const companyCode = currentView.substring(8).toUpperCase().replace(/\s+/g, ''); // Remove 'drivers-' prefix and normalize
      return <DriversView companyFilter={companyCode} />;
    }
    
    // Handle dynamic company inspection views (e.g., inspections-cs national)
    if (currentView.startsWith('inspections-') && currentView !== 'inspections') {
      const companyCode = currentView.substring(12).toUpperCase().replace(/\s+/g, ''); // Remove 'inspections-' prefix and normalize
      return <InspectionsView companyFilter={companyCode} />;
    }
    
    // Handle truck tracking view (e.g., /track/truck-id)
    if (currentView.startsWith('track/')) {
      const truckId = currentView.split('/')[1];
      return <TruckTrackingPage truckId={truckId} />;
    }
    
    // Handle dispatches/edit/:id
    if (currentView.startsWith('dispatches/edit/')) {
      const dispatchId = currentView.split('/')[2];
      return <EditDispatchPage dispatchId={dispatchId} onBack={() => setCurrentView('dispatches')} />;
    }

    // Handle dispatch detail view (e.g., /dispatches/DIS-123)
    if (currentView.startsWith('dispatches/')) {
      const dispatchId = currentView.split('/')[1];
      return <DispatchDetailView dispatchId={dispatchId} onBack={() => setCurrentView('dispatches')} />;
    }

    // Handle maintenance log detail view (e.g., /maintenance/log-id)
    if (currentView.startsWith('maintenance/') && currentView !== 'maintenance/') {
      return <MaintenanceView />;
    }
    
    // Handle loads/tracking
    if (currentView === 'loads/tracking') {
      return <TrackingView />;
    }
    
    // Handle loads/truck-photos
    if (currentView === 'loads/truck-photos') {
      return <LoadsTruckPhotosView />;
    }
    
    // Handle loads/trailer-photos
    if (currentView === 'loads/trailer-photos') {
      return <LoadsTrailerPhotosView />;
    }
    
    // Handle loads/driver-cdl-twic
    if (currentView === 'loads/driver-cdl-twic') {
      return <DriverCdlTwicView />;
    }
    
    // Handle loads/create
    if (currentView === 'loads/create') {
      return <CreateLoadPageComprehensive />;
    }
    
    // Handle loads/edit/:id
    if (currentView.startsWith('loads/edit/')) {
      const loadId = currentView.split('/')[2];
      return <EditLoadPage loadId={loadId} />;
    }

    // Handle company-1099/:driverName (URL-encoded driver label)
    if (currentView.startsWith('company-1099/')) {
      const encoded = currentView.slice('company-1099/'.length);
      const driverName = decodeURIComponent(encoded);
      return (
        <Company1099DriverDetailView
          driverName={driverName}
          onBack={() => setCurrentView('company-1099')}
        />
      );
    }
    
    // Handle loads/:id (detail view - read only)
    if (currentView.startsWith('loads/') && currentView !== 'loads/create' && !currentView.startsWith('loads/edit/')) {
      const loadId = currentView.split('/')[1];
      return <LoadDetailView loadId={loadId} />;
    }
    
    // Handle drivers/create
    if (currentView === 'drivers/create') {
      return <CreateDriverPage />;
    }
    
    // Handle drivers/:id (detail view)
    if (currentView.startsWith('drivers/') && currentView !== 'drivers/create' && !currentView.startsWith('drivers/edit/')) {
      const driverId = currentView.split('/')[1];
      return <DriverDetailPage driverId={driverId} />;
    }
    
    // Handle drivers/edit/:id
    if (currentView.startsWith('drivers/edit/')) {
      const driverId = currentView.split('/')[2];
      return <EditDriverPage driverId={driverId} />;
    }
    
    // Handle fleet/history
    if (currentView === 'fleet/history') {
      return <HistoryView />;
    }
    
    // Handle fleet/create
    if (currentView === 'fleet/create') {
      return <CreateTruckPage />;
    }
    
    // Handle fleet/edit/:id
    if (currentView.startsWith('fleet/edit/')) {
      const truckId = currentView.split('/')[2];
      return <EditTruckPage truckId={truckId} />;
    }
    
    // Handle fleet/:truckId (detail view)
    if (currentView.startsWith('fleet/') && currentView !== 'fleet/create' && currentView !== 'fleet/history' && !currentView.startsWith('fleet/edit/')) {
      const truckId = currentView.split('/')[1];
      return <TruckDetailPage truckId={truckId} />;
    }
    
    // Handle trailers/create
    if (currentView === 'trailers/create') {
      return <CreateTrailerPage />;
    }
    
    // Handle trailers/edit/:id
    if (currentView.startsWith('trailers/edit/')) {
      const trailerId = currentView.split('/')[2];
      return <EditTrailerPage trailerId={trailerId} />;
    }
    
    // Handle trailers/:id (detail view)
    if (currentView.startsWith('trailers/') && currentView !== 'trailers/create' && !currentView.startsWith('trailers/edit/')) {
      const trailerId = currentView.split('/')[1];
      return <TrailerDetailPage trailerId={trailerId} />;
    }
    
    // Handle maintenance routes - MaintenanceView handles its own internal routing
    // But exclude maintenance-statistics and maintenance-samsara which have their own routes
    if (currentView.startsWith('maintenance/') || currentView === 'maintenance') {
      return <MaintenanceView />;
    }
    
    // Handle customers/edit/:id
    if (currentView.startsWith('customers/edit/')) {
      const customerId = currentView.split('/')[2];
      return <EditCustomerPage customerId={customerId} />;
    }
    
    // Handle facility-locations/edit/:id
    if (currentView.startsWith('facility-locations/edit/')) {
      const facilityId = currentView.split('/')[2];
      return <EditFacilityLocationPage facilityId={facilityId} />;
    }
    
    // Handle factoring-companies/edit/:id
    if (currentView.startsWith('factoring-companies/edit/')) {
      const companyId = currentView.split('/')[2];
      return <EditFactoringCompanyPage companyId={companyId} />;
    }
    
    // Handle service-providers/edit/:id
    if (currentView.startsWith('service-providers/edit/')) {
      const providerId = currentView.split('/')[2];
      return <EditServiceProviderPage providerId={providerId} />;
    }
    
    switch (currentView) {
      case 'dashboard':
        return <AdvancedDashboard />;
      case 'samsara-location':
        return <SamsaraLocationView />;
      case 'eld-location':
        return <ELDPage initialTab="locations" />;
      case 'eld-hours':
        return <ELDPage initialTab="hours" />;
      case 'dispatch-board':
        return <DispatchBoardView />;
      // case 'dispatch': // Removed - using Excel page as dispatch board
      //   return <DispatchBoard />;
      case 'analytics':
        return <AdvancedAnalyticsView />;
      case 'loads':
        return <LoadsView />;
      case 'dispatches':
        return <DispatchesView />;
      case 'tracking':
        return <TrackingView />;
      case 'dispatchers-gross':
        return hasDispatchersGrossAccess ? <DispatchersGrossView /> : <AdvancedDashboard />;
      case 'ai-analytics':
        return <AIAnalyticsView />;
      case 'companies':
        return <CompaniesView />;
      case 'drivers':
        return <DriversView />;
      case 'fleet':
        return <FleetView />;
      case 'fleet-apollo':
        return <FleetView companyFilter="APOLLO" />;
      case 'fleet-tesla':
        return <FleetView companyFilter="TESLA" />;
      case 'fleet-tmark':
        return <FleetView companyFilter="TMARK" />;
      case 'trailers':
        return <TrailersView />;
      case 'maintenance':
        return <MaintenanceView />;
      case 'maintenance-statistics':
        return <MaintenanceStatistics />;
      case 'maintenance-samsara':
        return <SamsaraDiagnosticsView />;
      case 'maintenance-key-hider':
        return <KeyHiderView />;
      case 'truck-photos':
        return <TruckPhotosView />;
      case 'trailer-photos':
        return <TrailerPhotosView />;
      case 'truck-cleaning':
        return <TruckCleaningView />;
      case 'inspections':
        return <InspectionsView />;
      case 'inspections-apollo':
        return <InspectionsView companyFilter="apollo" />;
      case 'inspections-tmark':
        return <InspectionsView companyFilter="tmark" />;
      case 'inspections-tesla':
        return <InspectionsView companyFilter="tesla" />;
      case 'day-off':
        return <DayOffView />;
      case 'airplane-tickets-rec':
        return <AirplaneTicketsRecView />;
      case 'lift-rec':
        return <LiftRecView />;
      case 'eld':
        return <ELDView />;
      case 'analytics':
        return <RecruiterAdminDashboard />;
      case 'recruiting-performance':
        return <RecruiterPerformanceView />;
      case 'recruiting-daily':
        return <RecruiterDailyCRM />;
      case 'recruiting-leads':
        return <LeadsView />;
      case 'recruiting-all-drivers':
        return <RecruitingAllDriversView />;
      case 'recruiting-dolasci-odlasci':
        return <DolasciOdlasciView />;
      case 'reimbursements-hotel':
        return <HotelReimbursementsView />;
      case 'reimbursements-lyft':
        return <LyftReimbursementsView />;
      case 'reimbursements-airplane':
        return <AirplaneReimbursementsView />;
      case 'deductions':
        return <DeductionsView />;
      case 'deductions-maintenance':
        return <DeductionsMaintenanceView onViewDetails={handleMaintenanceLogView} />;
      case 'credits':
        return <CreditsView />;
      case 'scheduled-deductions':
        return <ScheduledDeductionsView />;
      case 'fuel':
        return <FuelView />;
      case 'statements':
        return <StatementsView />;
      case 'invoices':
        return <ReadyForBillingView />;
      case 'tabela':
        return <TabelaView />;
      case 'ifta':
        return <IFTAView />;
      case 'customers':
        return <CustomersView />;
      case 'facility-locations':
        return <FacilityLocationsView />;
      case 'factoring-companies':
        return <FactoringCompaniesView />;
      case 'service-providers':
        return <ServiceProvidersView />;
      case 'documents':
        return <DocumentsView />;
      case 'activity':
        return <ActivityLogView />;
      case 'settings':
        return <SettingsView />;
      case 'users':
        return <UserManagementView />;
      case 'unlock-accounts':
        return <UnlockAccountView />;
      case 'help':
        return <HelpCenterView />;
      case 'documentation':
        return <HelpCenterView />;
      case 'support-tickets':
        return <SupportTicketsView />;
      case 'my-tickets':
        return <MyTicketsView />;
      case 'notifications':
        return <NotificationsPage />;
      case 'new-trucks':
        return <NewTrucksView />;
      case 'company-expenses':
        return <CompanyExpensesView />;
      case 'company-1099':
        return <Company1099View />;
      case 'employee-statements':
        return <EmployeeStatementsView />;
      case 'employee-bonuses':
        return <EmployeeBonusesView />;
      case 'employee-expenses':
        return <EmployeeExpensesView />;
      default:
        // Handle dynamic company driver views (drivers-{company_code})
        if (currentView.startsWith('drivers-')) {
          const companyCode = currentView.replace('drivers-', '') as 'apollo' | 'tmark' | 'tesla';
          return <DriversView companyFilter={companyCode} />;
        }
        return <AdvancedDashboard />;
    }
  };

  // Render tracking page without layout for brokers
  if (currentView.startsWith('track/')) {
    return <ErrorBoundary key={currentView}>{renderView()}</ErrorBoundary>;
  }

  return (
    <>
      <CompactLayout currentView={currentView} onViewChange={setCurrentView}>
        {/* Keyed by view so a crash in one screen clears when navigating away,
            and never takes down the surrounding nav/layout. */}
        <ErrorBoundary key={currentView}>
          {renderView()}
        </ErrorBoundary>
      </CompactLayout>
      <AIAssistantChat />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
